import Cookies from 'js-cookie';
import { useContext, useEffect, useState } from 'react';
import constants from '../../../constants/constants';
import { Button, DatePicker, Empty, Form, message, Space, Spin, Tabs } from 'antd';
import Chart from 'react-google-charts';
import APIUtils from '../../../api/APIUtils';
import WebLinks from '../../../api/WebLinks';
import dayjs from 'dayjs';
import * as XLSX from "xlsx";
import { FileExcelOutlined } from "@ant-design/icons";
import { CommonContext } from '../../../Context';

const SpendVisuals = () => {
	const [loader, setLoader] = useState(false);
	const [chartData, setChartData] = useState([]);
	const [apiData, setApiData] = useState([]);
	const [activeKey, setActiveKey] = useState(constants.applications);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const secondLevelTabs = {
		DATE_RANGE: "1",
		THIS_MONTH: "2",
		LAST_12_MONTHS: "3",
	};
	const [secondLevelKey, setSecondLevelKey] = useState(secondLevelTabs.DATE_RANGE);
	const getDefaultDates = () => {
		const toDate = dayjs().format(constants.DateFormat2);
		const fromDate = dayjs().subtract(1, "month").format(constants.DateFormat2);

		return { fromDate, toDate };
	};
	const { fromDate: defaultFrom, toDate: defaultTo } = getDefaultDates();
	const [fromDate, setFromDate] = useState(defaultFrom);
	const [toDate, setToDate] = useState(defaultTo);
	const commonContext=useContext(CommonContext);
  const buyerId = commonContext.common.buyerId;

	const TAB_CONFIGSpendByDateRange = {
		[constants.applications]: {
			label: constants.Applications,
			apiRoute: WebLinks.GetSpendVisualsApplications,
			chartType: "LineChart",
			options: () => lineOptions(),
			hasDateFilter: true,
		},
		[constants.applicationsBySupplier]: {
			label: constants.ApplicationsBySupplier,
			apiRoute: WebLinks.GetSpendVisualsApplicationsBySuppliers,
			chartType: "LineChart",
			options: () => lineOptions(),
			hasDateFilter: true,
		},
		[constants.spend]: {
			label: constants.Spend,
			apiRoute: WebLinks.GetSpendVisualsSpend,
			chartType: "ColumnChart",
			options: () => columnOptions(),
			hasDateFilter: true,
		},
	};
	const TAB_CONFIGSpendThisMonth = {
		[constants.applicationsThisMonth]: {
			label: constants.ApplicationsThisMonth,
			apiRoute: WebLinks.GetSpendVisualsApplicationsThisMonth,
			chartType: "ColumnChart",
			options: () => columnOptions("Day"),
		},
		[constants.applicationsThisMonthBySupplier]: {
			label: constants.ApplicationsThisMonthBySupplier,
			apiRoute: WebLinks.GetSpendVisualsApplicationsThisMonthBySupplier,
			chartType: "LineChart",
			options: () => lineOptions(),
		},
		[constants.spendPerDayThisMonth]: {
			label: constants.SpendPerDayThisMonth,
			apiRoute: WebLinks.GetSpendVisualsSpendThisMonth,
			chartType: "ColumnChart",
			options: () => columnOptions("Day", "£"),
		},
		[constants.budgetVsSpendThisMonth]: {
			label: constants.BudgetVsSpendThisMonth,
			apiRoute: WebLinks.GetSpendVisualsBudgetVsSpendThisMonth,
			chartType: "ColumnChart",
			options: () => columnOptions(),
		},
		[constants.spendOfBudgetSoFarThisMonth]: {
			label: constants.SpendOfBudgetSoFarThisMonth,
			apiRoute: WebLinks.GetSpendVisualsBudgetSpendThisMonthBySupplier,
			chartType: "ColumnChart",
			options: () => columnOptions(),
		},
	};
	const TAB_CONFIGSpendLast12Months = {
		[constants.applicationVolumeLast12Months]: {
			label: constants.ApplicationVolumeLast12Months,
			apiRoute: WebLinks.GetSpendVisualsApplicationsLastMonths,
			chartType: "ColumnChart",
			options: () => columnOptions("Month"),
		},
		[constants.spendPerMonthLast24Months]: {
			label: constants.SpendPerMonthLast24Months,
			apiRoute: WebLinks.GetSpendVisualsSpendLast24Months,
			chartType: "ColumnChart",
			options: () => columnOptions(),
		},
		[constants.jobsPostedPerMonth]: {
			label: constants.JobsPostedPerMonth,
			apiRoute: WebLinks.GetSpendVisualsJobsPostedPerMonth,
			chartType: "ColumnChart",
			options: () => columnOptions("Month"),
		},
	};
	const getTabConfig = () => {
		switch (secondLevelKey) {
			case secondLevelTabs.DATE_RANGE:
				return TAB_CONFIGSpendByDateRange;
			case secondLevelTabs.THIS_MONTH:
				return TAB_CONFIGSpendThisMonth;
			case secondLevelTabs.LAST_12_MONTHS:
				return TAB_CONFIGSpendLast12Months;
			default:
				return {};
		}
	};
	const TAB_CONFIG = getTabConfig();
	const tabsList = Object.entries(TAB_CONFIG).map(([key, val]) => ({
		key,
		label: val.label,
	}));
	useEffect(() => {
		if (!buyerId || !activeKey) return;

		if (TAB_CONFIG[activeKey]?.hasDateFilter &&(!fromDate || !toDate)) {
			return;
		}
		getDashboardUrl();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeKey, buyerId, fromDate, toDate, secondLevelKey]);

	const lineOptions = (titleX = "", titleY = "") => ({
		titlePosition: "none",
		curveType: "none",
		chartArea: { top: 20, width: "80%", height: "80%" },
		legend: {
			position: "bottom",
			alignment: "center",
		},
		hAxis: {
			title: titleX,
			gridlines: {
				color: "#eee",
			},
			textPosition: "center",
			minValue: 0,
		},
		vAxis: {
			title: titleY,
			minValue: 0,
		},
		pointSize: 5,
		lineWidth: 2,
	});
	const columnOptions = (titleX = "", titleY = "") => ({
		titlePosition: "none",
		chartArea: { top: 20, width: "80%", height: "80%" },
		legend: {
			position: "bottom",
			alignment: "center",
		},
		hAxis: {
			title: titleX,
			format: "0",
		},
		vAxis: {
			title: titleY,
			minValue: 0,
			format: "0",
		},
	});

	const formattedText = (apiData) => {
		let formattedData = [];
		let titleText = [];
		switch (activeKey) {
			case constants.applications:
				titleText = ["Date", "Total Apps", "Quality Fail", "Interview", "Hired"]
				formattedData = [
					titleText,
					...apiData.map((item) => [
						item.date,
						item.totalApps ?? 0,
						item.qualityFail ?? 0,
						item.interview ?? 0,
						item.hired ?? 0,
					]),
				];
				break;
			case constants.applicationsBySupplier:
				const suppliersData = [...new Set(apiData.map(item => item?.name))];
				const datesData = [...new Set(apiData.map(item => item?.dateOnly))]?.sort();
				titleText = ["Date", ...suppliersData];
				const dataMapData = {};
				apiData.forEach(item => {
					const date = item?.dateOnly;
					if (!dataMapData[date]) dataMapData[date] = {};
					dataMapData[date][item?.name] = item.totalApps ?? 0;
				});
				formattedData = [
					titleText,
					...datesData.map(date => [
						date,
						...suppliersData.map(name => dataMapData[date]?.[name] ?? 0)
					])
				];
				break;
			case constants.spend:
				titleText = ["Days", "Cost"]
				formattedData = [
					titleText,
					...apiData.map((item) => [
						item?.date,
						item?.cost ? parseInt(item?.cost) : 0,
					]),
				];
				break;
			case constants.applicationsThisMonth:
				titleText = ["Days", "Total Apps", "Quality Fail", "Interview", "Hired"]
				formattedData = [
					titleText,
					...apiData.map((item) => [
						item?.days?.toString(),
						item.totalApps ?? 0,
						item.qualityFail ?? 0,
						item.interview ?? 0,
						item.hired ?? 0,
					]),
				];
				break;
			case constants.applicationsThisMonthBySupplier:
				const suppliers = [...new Set(apiData.map(item => item?.name))];
				const dates = [...new Set(apiData.map(item => item?.days))]
					.sort((a, b) => a - b);
				titleText = ["Day", ...suppliers];
				const dataMapObj = {};
				apiData.forEach(item => {
					const day = String(item?.days);
					if (!dataMapObj[day]) dataMapObj[day] = {};
					dataMapObj[day][item?.name] = item.totalApps ?? 0;
				});
				formattedData = [
					titleText,
					...dates.map(day => {
						const d = String(day);
						return [
							d,
							...suppliers.map(name => dataMapObj[d]?.[name] ?? 0)
						];
					})
				];
				break;
			case constants.spendPerDayThisMonth:
				titleText = ["Days", "Cost"]
				formattedData = [
					titleText,
					...apiData.map((item) => [
						item?.days,
						item?.cost ? parseInt(item?.cost) : 0,
					]),
				];
				break;
			case constants.spendPerMonthLast24Months:
				const years = [...new Set(apiData.map(i => i.year))].sort();
				const months = [...new Set(apiData.map(i => i.month))].sort((a, b) => a - b);
				const map = {};
				apiData.forEach(i => {
					if (!map[i.month]) map[i.month] = {};
					map[i.month][i.year] = i.spend ?? 0;
				});
				formattedData = [
					["Month", ...years.map(String)],
					...months.map(m => [
						m.toString(),
						...years.map(y => map[m]?.[y] ?? 0)
					])
				];
				break;
			case constants.applicationVolumeLast12Months:
				titleText = ["Month", "Total Apps", "Quality Fail", "Interview", "Hired"];
				const sortedData = [...apiData].sort((a, b) => a.yyyyMM - b.yyyyMM);
				formattedData = [
					titleText,
					...sortedData.map(item => [
						item.month,
						item.totalApps ?? 0,
						item.qualityFail ?? 0,
						item.interview ?? 0,
						item.hired ?? 0,
					])
				];
				break;
			case constants.jobsPostedPerMonth:
				titleText = ["Month", "Jobs Posted"]
				formattedData = [
					titleText,
					...apiData.map((item) => [
						item?.month,
						item?.jobsPosted ?? 0,
					]),
				];
				break;
			case constants.budgetVsSpendThisMonth:
				titleText = ["Month", "Spend", "Budget"];
				formattedData = [
					titleText,
					[
						"This Month",
						apiData?.spend ? parseInt(apiData?.spend) : 0,
						apiData?.budget ? parseInt(apiData?.budget) : 0
					]
				];
				break;
			case constants.spendOfBudgetSoFarThisMonth:
				titleText = ["Supplier", "Percentage Spend"];
				formattedData = [
					titleText,
					...apiData.map((item) => [
						item?.name || "-",
						item?.percentagespend ?? 0,
					])
				];
				break;
			default:
				formattedData = [];
		}
		return formattedData;
	}
	const buildDashboardUrl = (activeKey) => {
		let params = `buyerId=${buyerId}`;
		if (TAB_CONFIG[activeKey]?.hasDateFilter) {
			params += `&startDate=${fromDate}&endDate=${toDate}`;
		}
		if (activeKey === constants.applicationVolumeLast12Months || activeKey === constants.jobsPostedPerMonth) {
			params += `&months=${12}`
		}
		return `${TAB_CONFIG[activeKey]?.apiRoute}?${params}`;
	};

	const getDashboardUrl = async () => {
		setLoader(true);
		try {
			const url = buildDashboardUrl(activeKey);
			const response = await APIUtils.apiCall('GET', url);
			if (!response.isCatch && response.resultObj.statusCode === "2000") {
				const apiData = response.resultObj.data || [];
				setChartData(formattedText(apiData));
				setApiData(apiData);
			} else {
				message.error(response?.resultObj?.message || "Something went wrong");
			}
		} catch (error) {
			console.error("API Error:", error);
			message.error("Failed to fetch dashboard data");
		} finally {
			setLoader(false);
		}
	};

	const handleDateChange = (type, date, dateString) => {
		if (type === "from") {
			if (toDate && dayjs(dateString).isAfter(dayjs(toDate))) {
				message.error("From date cannot be after To date");
				return;
			}
			setFromDate(dateString);
		}

		if (type === "to") {
			if (fromDate && dayjs(dateString).isBefore(dayjs(fromDate))) {
				message.error("To date cannot be before From date");
				return;
			}
			setToDate(dateString);
		}
	};

	const disableDate = (type, current) => {
		if (!current || current > dayjs().endOf("day")) return true;

		const compareDate = type === "from" ? toDate : fromDate;
		if (!compareDate) return false;

		return type === "from"
			? current > dayjs(compareDate)
			: current < dayjs(compareDate);
	};

	const handleChangeActiveKey = (e) => {
		setActiveKey(e)
	};

	const getChart = (type, options) => (
		loader ? (
			<Spin className='min-h-[50vh] flex justify-center items-center' />
		) : !hasData ? (
			<Empty className='mt-5' description={constants.NO_DATA} />
		) : (
			<Chart
				key={activeKey}
				chartType={type}
				height="600px"
				data={chartData}
				options={options}
			/>
		)
	)
	const hasData = chartData ? chartData?.length > 1 : false;
	const downloadFile = (tabName) => {
		try {
			if (!apiData || apiData.length === 0) {
				message.error("No data to download");
				return;
			}
			setDownloadLoading(true);
			let excelData = [];
			switch (activeKey) {
				case constants.applications:
					excelData = apiData.map(item => ({
						"Date": item.date,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item?.cost ? parseInt(item.cost) : 0,
					}));
					break;
				case constants.applicationsBySupplier:
					excelData = apiData.map(item => ({
						"Date": item?.dateOnly,
						"Name": item?.name,
						"TotalApps": item?.totalApps ?? 0,
					}));
					break;
				case constants.spend:
					excelData = apiData.map(item => ({
						"Date": item?.date,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item?.cost ? parseInt(item.cost) : 0,
					}));
					break;
				case constants.applicationsThisMonth:
					excelData = apiData.map(item => ({
						"Day": item?.days,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item?.cost ? parseInt(item.cost) : 0,
					}));
					break;
				case constants.applicationsThisMonthBySupplier:
					excelData = apiData.map(item => ({
						"Day": item?.days,
						"Name": item?.name,
						"LogoURL": item?.logoURL,
						"TotalApps": item?.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item?.cost ? parseInt(item.cost) : 0,
					}));
					break;
				case constants.spendPerDayThisMonth:
					excelData = apiData.map(item => ({
						"Day": item?.days,
						"TotalApps": item?.totalApps ?? 0,
						"Cost": item?.cost ? parseInt(item.cost) : 0,
					}));
					break;
				case constants.spendPerMonthLast24Months:
					excelData = apiData.map(item => ({
						"Month": item?.month,
						"Year": item?.year,
						"Spend": item?.spend ? parseInt(item?.spend) : 0
					}));
					break;
				case constants.applicationVolumeLast12Months:
					excelData = apiData.map(item => ({
						"Year": item.yyyyMM,
						"Month": item.month,
						"Total Apps": item.totalApps ?? 0,
						"Quality Fail": item.qualityFail ?? 0,
						"Interview": item.interview ?? 0,
						"Hired": item.hired ?? 0,
						"Cost": item?.cost ? parseInt(item.cost) : 0,
					}));
					break;
				case constants.jobsPostedPerMonth:
					excelData = apiData.map(item => ({
						"Month": item?.month,
						"JobsPosted": item?.jobsPosted ?? 0,
					}));
					break;
				case constants.budgetVsSpendThisMonth:
					excelData = [{
						"Spend": apiData?.spend ? parseInt(apiData?.spend) : 0,
						"Budget": apiData?.budget ? parseInt(apiData?.budget) : 0
					}];
					break;
				case constants.spendOfBudgetSoFarThisMonth:
					excelData = apiData.map(item => ({
						"Supplier": item?.name || "-",
						"Budget": item?.budget ? parseInt(item?.budget) : 0,
						"PercentageSpend": item?.percentagespend ?? 0,
					}));
					break;
				default:
					message.warning("Download failed");
					setDownloadLoading(false);
					return;
			}
			const worksheet = XLSX.utils.json_to_sheet(excelData);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
			const fileName = TAB_CONFIG[activeKey]?.hasDateFilter
				? `${tabName} ${fromDate} - ${toDate}.xlsx`
				: `${tabName}.xlsx`;
			setTimeout(() => {
				XLSX.writeFile(workbook, fileName);
				setDownloadLoading(false);
			}, 300);
		} catch (err) {
			setDownloadLoading(false);
			message.error("Error downloading file");
		}
	};
	const renderHeader = (tabName) => (
		<div
			className={`justify-end flex flex-wrap`}
		>
			<Button
				className='bg-green-800 text-white hover:!text-white hover:!border-green-500'
				icon={<FileExcelOutlined />}
				onClick={() => downloadFile(tabName)}
				loading={downloadLoading}
			>
				Download as Excel
			</Button>
		</div>
	);
	const displayDate = () => {
		return (
			<Form>
				<Space>
					<Form.Item label="From" className="mb-3">
						<DatePicker
							onChange={(date, dateString) => handleDateChange("from", date, dateString)}
							value={fromDate ? dayjs(fromDate) : null}
							disabledDate={(current) => disableDate("from", current)}
							allowClear={false}
						/>
					</Form.Item>
					<Form.Item label="To" className="mb-3">
						<DatePicker
							onChange={(date, dateString) => handleDateChange("to", date, dateString)}
							value={toDate ? dayjs(toDate) : null}
							disabledDate={(current) => disableDate("to", current)}
							allowClear={false}
						/>
					</Form.Item>
				</Space>
			</Form>
		);
	};
	const secondLevelTabItems = [
		{
			key: secondLevelTabs.DATE_RANGE,
			label: constants.AppsSpendByDateRange,
		},
		{
			key: secondLevelTabs.THIS_MONTH,
			label: constants.AppsSpendThisMonth,
		},
		{
			key: secondLevelTabs.LAST_12_MONTHS,
			label: constants.JobsAppsSpendLast12Months,
		},
	];
	return (
		<div>
			<Tabs
				activeKey={secondLevelKey}
				onChange={(key) => {
					setSecondLevelKey(key);
					const firstTab = Object.keys(
						key === secondLevelTabs.DATE_RANGE
							? TAB_CONFIGSpendByDateRange
							: key === secondLevelTabs.THIS_MONTH
								? TAB_CONFIGSpendThisMonth
								: TAB_CONFIGSpendLast12Months
					)[0];
					setActiveKey(firstTab);
				}}
				items={secondLevelTabItems}
			/>
			{secondLevelKey === secondLevelTabs.DATE_RANGE && displayDate()}
			<Tabs
				activeKey={activeKey}
				onChange={handleChangeActiveKey}
				items={tabsList.map(({ key, label }) => {
					const config = TAB_CONFIG[key];
					return {
						key,
						label,
						children: (
							<>
								{renderHeader(label)}
								<div className="text-center my-3 text-xl">
									{label} {config?.hasDateFilter ? ` - ${fromDate} to ${toDate}` : ""}
								</div>
								{getChart(config.chartType, config.options())}
							</>
						),
					};
				})}
			/>
		</div>
	);
};
export default SpendVisuals;
